import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Title from "../components/header/Title";
import RefundService, { IRefund } from "../services/RefundService";
import RefundDetailDialog from "../components/refund/RefundDetailDialog";
import { unwrapPage, formatDateArray, getErrorMessage } from "../services/apiUtils";
import { isRequested, refundStatusLabel, refundStatusColor as statusColor } from "../services/refundStatusUtils";

const Refunds: React.FC = () => {
  const [tab, setTab] = useState<"PROCESS" | "HISTORY">("PROCESS");
  const [allRefunds, setAllRefunds] = useState<IRefund[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [processPage, setProcessPage] = useState(0);
  const [processPageSize, setProcessPageSize] = useState(10);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(10);

  const [detailRefundId, setDetailRefundId] = useState<number | null>(null);
  const [detailListStatus, setDetailListStatus] = useState<string | undefined>(undefined);

  const loadRefunds = async () => {
    setLoading(true);
    setError(null);
    try {
      
      
      
      const probe: any = await RefundService.filterRefunds({ page: 0, size: 1 });
      const { totalElements } = unwrapPage<IRefund>(probe);
      const size = Math.max(totalElements, 1);
      const res: any = await RefundService.filterRefunds({ page: 0, size });
      const { content } = unwrapPage<IRefund>(res);
      setAllRefunds(content);
    } catch (err: any) {
      setAllRefunds([]);
      setError(
        getErrorMessage(
          err,
          "Refunds could not be loaded. Please make sure the Refunds API is reachable."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRefunds();
  }, []);

  
  
  
  const toProcessRows = useMemo(
    () => allRefunds.filter((r) => isRequested(r.status)),
    [allRefunds]
  );
  const historyRows = useMemo(
    () => allRefunds.filter((r) => !isRequested(r.status)),
    [allRefunds]
  );

  const openDetail = (row: IRefund) => {
    setDetailRefundId(row.refundId);
    setDetailListStatus(row.status);
  };

  const baseColumns: GridColDef[] = [
    { field: "refundId", headerName: "Refund ID", flex: 0.5 },
    {
      field: "orderNumber",
      headerName: "Order",
      flex: 1,
      valueGetter: (params: any) =>
        params.row.orderNumber || params.row.orderId,
    },
    { field: "userName", headerName: "Customer", flex: 0.8 },
    { field: "userEmail", headerName: "Email", flex: 1 },
    {
      field: "totalAmount",
      headerName: "Amount",
      flex: 0.6,
      valueFormatter: (params: any) =>
        params.value != null ? `Rs. ${params.value}` : "-",
    },
    {
      field: "requestedAt",
      headerName: "Requested",
      flex: 0.9,
      valueFormatter: (params: any) => formatDateArray(params.value),
    },
    {
      field: "processedAt",
      headerName: "Processed",
      flex: 0.9,
      valueFormatter: (params: any) => formatDateArray(params.value),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params: any) => (
        <Chip
          label={refundStatusLabel(params.value)}
          color={statusColor(params.value) as any}
          size="small"
        />
      ),
    },
  ];

  const processColumns: GridColDef[] = [
    ...baseColumns,
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => openDetail(params.row)}>
            Process
          </Button>
        </Box>
      ),
    },
  ];

  const historyColumns: GridColDef[] = [
    ...baseColumns,
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => openDetail(params.row)}>
            View
          </Button>
        </Box>
      ),
    },
  ];

  const renderGrid = (
    rows: IRefund[],
    columns: GridColDef[],
    page: number,
    pageSize: number,
    setPage: (p: number) => void,
    setPageSize: (s: number) => void,
    emptyMessage: string
  ) =>
    loading ? (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <CircularProgress />
      </Box>
    ) : rows.length === 0 && !error ? (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    ) : (
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.refundId}
        paginationMode="client"
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />
    );

  return (
    <Box>
      <Title title="Refunds" subtitle="Review and action customer refund requests" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2 }}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab
          value="PROCESS"
          label={`To Process${!loading ? ` (${toProcessRows.length})` : ""}`}
        />
        <Tab
          value="HISTORY"
          label={`History${!loading ? ` (${historyRows.length})` : ""}`}
        />
      </Tabs>

      <Box sx={{ height: 600, width: "100%", bgcolor: "#fff" }}>
        {tab === "PROCESS"
          ? renderGrid(
              toProcessRows,
              processColumns,
              processPage,
              processPageSize,
              setProcessPage,
              setProcessPageSize,
              "No refund requests waiting on a decision."
            )
          : renderGrid(
              historyRows,
              historyColumns,
              historyPage,
              historyPageSize,
              setHistoryPage,
              setHistoryPageSize,
              "No processed refunds yet."
            )}
      </Box>

      <RefundDetailDialog
        refundId={detailRefundId}
        listStatus={detailListStatus}
        onClose={() => {
          setDetailRefundId(null);
          setDetailListStatus(undefined);
        }}
        onProcessed={loadRefunds}
      />
    </Box>
  );
};

export default Refunds;
